import Logger from '../logger';
import avatarPlaceholder from '../avatar-placeholder.png';

const logger = new Logger('AvatarUtils');

/**
 * Create a canvas-based video track with avatar placeholder
 * This keeps the video stream active but shows avatar instead of camera
 * @param {number} width - Canvas width (default: 640)
 * @param {number} height - Canvas height (default: 480)
 * @param {Object} avatarCanvasRef - Object to store canvas reference { current: canvas }
 * @param {Object} avatarRenderIntervalRef - Object to store interval reference { current: interval }
 * @param {Function} getAvatarUri - Function to get uploaded avatar URI from store
 */
export const createAvatarVideoTrack = async (width = 640, height = 480, avatarCanvasRef, avatarRenderIntervalRef, getAvatarUri = null) => {
	return new Promise((resolve, reject) => {
		try {
			// Use fixed dimensions to ensure consistent quality and size
			const FIXED_WIDTH = 640;
			const FIXED_HEIGHT = 480;
			
			console.log(`[AVATAR TRACK] Creating avatar video track with fixed size: ${FIXED_WIDTH}x${FIXED_HEIGHT}`);
			
			// Always create a fresh canvas to ensure we get a new video track
			// Clean up old canvas and interval if they exist
			if (avatarCanvasRef.current && avatarRenderIntervalRef.current) {
				try {
					clearInterval(avatarRenderIntervalRef.current);
					avatarRenderIntervalRef.current = null;
					// Stop any existing streams from the old canvas
					if (avatarCanvasRef.current.captureStream) {
						const oldStream = avatarCanvasRef.current.captureStream();
						oldStream.getTracks().forEach(track => track.stop());
					}
				} catch (e) {
					console.warn('[AVATAR TRACK] Error cleaning up old canvas:', e);
				}
			}
			
			// Create a new canvas
			const canvas = document.createElement('canvas');
			canvas.width = FIXED_WIDTH;
			canvas.height = FIXED_HEIGHT;
			avatarCanvasRef.current = canvas;
			
			const ctx = canvas.getContext('2d');

			// Fill with black background
			ctx.fillStyle = '#000000';
			ctx.fillRect(0, 0, FIXED_WIDTH, FIXED_HEIGHT);

			// Load avatar image
			const img = new Image();
			img.crossOrigin = 'anonymous';
			
			// Get uploaded avatar from Redux store, or use default placeholder
			const uploadedAvatarUri = getAvatarUri ? getAvatarUri() : null;
			const defaultAvatarSrc = typeof avatarPlaceholder === 'string' ? avatarPlaceholder : avatarPlaceholder.default || avatarPlaceholder;
			const imageSrc = uploadedAvatarUri || defaultAvatarSrc;
			
			console.log('[AVATAR TRACK] Loading avatar image from:', uploadedAvatarUri ? 'uploaded user image' : 'default placeholder');
			img.src = imageSrc;
			
			img.onload = () => {
				try {
					console.log('[AVATAR TRACK] Avatar image loaded successfully, dimensions:', img.width, 'x', img.height);
					
					// Calculate size to fit avatar in center (30% of canvas, max 200px)
					const avatarSize = Math.min(FIXED_WIDTH * 0.3, FIXED_HEIGHT * 0.3, 200);
					const x = (FIXED_WIDTH - avatarSize) / 2;
					const y = (FIXED_HEIGHT - avatarSize) / 2;

					console.log('[AVATAR TRACK] Drawing avatar at:', x, y, 'size:', avatarSize);

					// Draw function that will be called continuously
					const drawAvatar = () => {
						// Clear and redraw to ensure continuous rendering
						ctx.fillStyle = '#000000';
						ctx.fillRect(0, 0, FIXED_WIDTH, FIXED_HEIGHT);
						ctx.drawImage(img, x, y, avatarSize, avatarSize);
					};

					// Initial draw
					drawAvatar();

					// Create video track from canvas stream
					const stream = canvas.captureStream(30); // 30 FPS
					const videoTrack = stream.getVideoTracks()[0];

					if (!videoTrack) {
						console.error('[AVATAR TRACK] Failed to create video track from canvas stream');
						reject(new Error('Failed to create video track from canvas'));
						return;
					}

					console.log('[AVATAR TRACK] Video track created:', {
						id: videoTrack.id,
						label: videoTrack.label,
						enabled: videoTrack.enabled,
						readyState: videoTrack.readyState,
						kind: videoTrack.kind
					});

					// Keep canvas rendering continuously (required for video track)
					// Only create new interval if one doesn't exist
					if (!avatarRenderIntervalRef.current) {
						avatarRenderIntervalRef.current = setInterval(() => {
							drawAvatar();
						}, 33); // ~30 FPS
						console.log('[AVATAR TRACK] Started rendering interval');
					} else {
						console.log('[AVATAR TRACK] Reusing existing rendering interval');
					}

					// Add cleanup when track ends
					videoTrack.addEventListener('ended', () => {
						console.log('[AVATAR TRACK] Video track ended, cleaning up');
						if (avatarRenderIntervalRef.current) {
							clearInterval(avatarRenderIntervalRef.current);
							avatarRenderIntervalRef.current = null;
						}
					});

					logger.debug('Avatar video track created successfully');
					console.log('[AVATAR TRACK] ✅ Avatar video track created successfully');
					resolve(videoTrack);
				} catch (error) {
					console.error('[AVATAR TRACK] Error creating avatar video track:', error);
					logger.error('Error creating avatar video track:', error);
					reject(error);
				}
			};

			img.onerror = (error) => {
				console.error('[AVATAR TRACK] Failed to load avatar image:', error, 'Image src:', imageSrc);
				logger.error('Failed to load avatar image:', error);
				
				// Fallback: create a simple colored circle with text
				const centerX = FIXED_WIDTH / 2;
				const centerY = FIXED_HEIGHT / 2;
				const radius = Math.min(FIXED_WIDTH, FIXED_HEIGHT) * 0.15;
				
				// Draw background
				ctx.fillStyle = '#1a1a1a';
				ctx.fillRect(0, 0, FIXED_WIDTH, FIXED_HEIGHT);
				
				// Draw circle
				ctx.fillStyle = '#4A90E2';
				ctx.beginPath();
				ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
				ctx.fill();
				
				// Draw text
				ctx.fillStyle = '#FFFFFF';
				ctx.font = 'bold 48px Arial';
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.fillText('👤', centerX, centerY);

				// Create video track from canvas stream
				const stream = canvas.captureStream(30);
				const videoTrack = stream.getVideoTracks()[0];

				if (!videoTrack) {
					console.error('[AVATAR TRACK] Failed to create video track from canvas (fallback)');
					reject(new Error('Failed to create video track from canvas'));
					return;
				}

				console.log('[AVATAR TRACK] Video track created with fallback:', {
					id: videoTrack.id,
					label: videoTrack.label,
					enabled: videoTrack.enabled,
					readyState: videoTrack.readyState
				});

				// Keep rendering - only create new interval if one doesn't exist
				if (!avatarRenderIntervalRef.current) {
					avatarRenderIntervalRef.current = setInterval(() => {
						ctx.fillStyle = '#1a1a1a';
						ctx.fillRect(0, 0, FIXED_WIDTH, FIXED_HEIGHT);
						ctx.fillStyle = '#4A90E2';
						ctx.beginPath();
						ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
						ctx.fill();
						ctx.fillStyle = '#FFFFFF';
						ctx.font = 'bold 48px Arial';
						ctx.textAlign = 'center';
						ctx.textBaseline = 'middle';
						ctx.fillText('👤', centerX, centerY);
					}, 33);
					console.log('[AVATAR TRACK] Started rendering interval (fallback)');
				} else {
					console.log('[AVATAR TRACK] Reusing existing rendering interval (fallback)');
				}

				videoTrack.addEventListener('ended', () => {
					if (avatarRenderIntervalRef.current) {
						clearInterval(avatarRenderIntervalRef.current);
						avatarRenderIntervalRef.current = null;
					}
				});

				logger.debug('Avatar video track created with fallback');
				console.log('[AVATAR TRACK] ✅ Avatar video track created with fallback');
				resolve(videoTrack);
			};
		} catch (error) {
			console.error('[AVATAR TRACK] Error in createAvatarVideoTrack:', error);
			logger.error('Error in createAvatarVideoTrack:', error);
			reject(error);
		}
	});
};

